import React, { useState } from "react";
import axios from "axios";
import { Loader2, Check } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { API } from "../../App";

const UNASSIGNED = "__unassigned__";

// Assignment-only quick selector — saves immediately via PATCH /jobs/{id}/assign
// (updates ONLY the assignment fields; full job payload is never sent).
const QuickAssignTech = ({ job, teamMembers = [], onAssigned, className = "" }) => {
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const techs = teamMembers.filter((t) => (t.status || "active") === "active");
  const currentInList =
    !job?.assigned_to || techs.some((t) => (t.technician_id || t.member_id) === job.assigned_to);

  const handleChange = async (v) => {
    const newTech = v === UNASSIGNED ? "" : v;
    if ((newTech || "") === (job?.assigned_to || "")) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/jobs/${job.job_id}/assign`,
        { assigned_to: newTech },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      onAssigned?.(job.job_id, {
        assigned_to: res.data.assigned_to || "",
        assigned_to_name: res.data.assigned_to_name || "",
      });
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
      toast.success(
        res.data.assigned_to_name
          ? `Assigned to ${res.data.assigned_to_name}`
          : "Technician unassigned"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Select value={job?.assigned_to || UNASSIGNED} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger className="h-8 text-xs input-glass flex-1 min-w-0" data-testid="quick-assign-select">
          <SelectValue placeholder="Assign technician" />
        </SelectTrigger>
        <SelectContent className="z-[70]">
          <SelectItem value={UNASSIGNED} data-testid="quick-assign-option-unassigned">Unassigned</SelectItem>
          {techs.map((t) => {
            const id = t.technician_id || t.member_id;
            return (
              <SelectItem key={id} value={id} data-testid={`quick-assign-option-${id}`}>
                {t.name}
              </SelectItem>
            );
          })}
          {/* Keep a legacy/inactive assignee visible so the field isn't blank */}
          {!currentInList && (
            <SelectItem value={job.assigned_to}>{job.assigned_to_name || "Current technician"}</SelectItem>
          )}
        </SelectContent>
      </Select>
      {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground flex-shrink-0" data-testid="quick-assign-saving" />}
      {!saving && justSaved && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" data-testid="quick-assign-saved" />}
    </div>
  );
};

export default QuickAssignTech;
